import { useEffect, useState } from "react";
import { useLang } from "../LanguageContext";

// Main facade — full quality, no blur
const HERO_IMAGE = "https://customer-assets.emergentagent.com/job_torre-amat-events/artifacts/khsvkaqk_portda.png";
const DOSSIER_URL = "https://customer-assets.emergentagent.com/job_torre-amat-events/artifacts/rbmb6e4b_D%C3%ADptic%20Torre%20Amat.pdf";

// Delay (ms) before the text block starts revealing after image appears
const IMAGE_ONLY_DELAY = 900;
// Delay between heading1 reveal and typing start
const TYPE_START_DELAY = IMAGE_ONLY_DELAY + 600;
// Time per character (ms) in the typewriter effect
const TYPE_SPEED = 95;

export default function HeroSection() {
  const { t, lang } = useLang();
  const fullText = t.hero.heading2;
  const [typed, setTyped] = useState("");
  const [typingDone, setTypingDone] = useState(false);
  const [overlayOn, setOverlayOn] = useState(false);

  // Typewriter effect — runs once per language change, no loop
  useEffect(() => {
    setTyped("");
    setTypingDone(false);
    setOverlayOn(false);

    // Dim the background slightly once the content begins revealing
    const overlayTimeout = setTimeout(() => setOverlayOn(true), IMAGE_ONLY_DELAY);

    let index = 0;
    let intervalId;
    const startTimeout = setTimeout(() => {
      intervalId = setInterval(() => {
        index += 1;
        setTyped(fullText.slice(0, index));
        if (index >= fullText.length) {
          clearInterval(intervalId);
          setTypingDone(true);
        }
      }, TYPE_SPEED);
    }, TYPE_START_DELAY);

    return () => {
      clearTimeout(overlayTimeout);
      clearTimeout(startTimeout);
      if (intervalId) clearInterval(intervalId);
    };
  }, [fullText]);

  const scrollTo = (id) => {
    const el = document.querySelector(id);
    if (el) el.scrollIntoView({ behavior: "smooth" });
  };

  // Compute approximate total typing time for staggering the rest of the hero
  const typingTotalMs = TYPE_START_DELAY + fullText.length * TYPE_SPEED;

  return (
    <section
      id="home"
      data-testid="hero-section"
      className="relative w-full h-screen min-h-[640px] overflow-hidden"
    >
      {/* Background image — cover full screen, anchored top-center */}
      <div
        className="absolute inset-0 hero-zoom"
        style={{
          backgroundImage: `url(${HERO_IMAGE})`,
          backgroundSize: "cover",
          backgroundPosition: "center center",
          backgroundRepeat: "no-repeat",
        }}
      />

      {/* Dark overlay — starts transparent so image shows original, dims in after delay */}
      <div
        className={`absolute inset-0 bg-[#1A1A18] transition-opacity duration-[1200ms] ease-out ${
          overlayOn ? "opacity-[0.52]" : "opacity-0"
        }`}
      />

      {/* Grain texture */}
      <div className="absolute inset-0 opacity-[0.04] grain-texture pointer-events-none" />

      {/* Bottom fade */}
      <div className="absolute bottom-0 left-0 right-0 h-48 bg-gradient-to-t from-[#1A1A18]/70 to-transparent" />

      {/* Content — absolutely fixed to bottom, always far from navbar */}
      <div className="absolute bottom-0 left-0 right-0 z-10 pb-16 md:pb-24">
        <div className="max-w-7xl mx-auto px-6 md:px-12">

          {/* Tagline sits directly above h1, never near top */}
          <p
            data-testid="hero-tagline"
            className="text-[10px] md:text-xs uppercase tracking-[0.4em] text-[#C8A96E] mb-3 hero-reveal"
            style={{
              fontFamily: "Lato, sans-serif",
              animationDelay: `${IMAGE_ONLY_DELAY}ms`,
            }}
          >
            {t.hero.tagline}
          </p>

          {/* Main heading — slightly smaller in English so the longer phrase still fits in 2 lines */}
          <h1
            data-testid="hero-heading"
            className={`font-light text-[#F5F0E8] leading-[0.95] mb-5 ${
              lang === "en"
                ? "text-[2.25rem] sm:text-[2.75rem] md:text-[3.5rem] lg:text-[4.25rem]"
                : "text-[2.75rem] sm:text-[3.5rem] md:text-[4.25rem] lg:text-[5.25rem]"
            }`}
            style={{ fontFamily: "'Cormorant Garamond', serif" }}
          >
            <span
              className="block hero-reveal"
              style={{ animationDelay: `${IMAGE_ONLY_DELAY + 200}ms` }}
            >
              {t.hero.heading1}
            </span>
            <span
              className="block italic text-[#C8A96E] min-h-[2.2em]"
              data-testid="hero-typing"
            >
              {typed}
              {!typingDone && <span className="typing-caret" aria-hidden="true" />}
            </span>
          </h1>

          {/* Subheading */}
          <p
            data-testid="hero-subheading"
            className="text-sm md:text-base text-[#F5F0E8]/80 max-w-3xl mb-8 leading-relaxed hero-reveal"
            style={{
              fontFamily: "Lato, sans-serif",
              fontWeight: 300,
              animationDelay: `${typingTotalMs + 200}ms`,
            }}
          >
            {t.hero.subheading}
          </p>

        {/* CTAs */}
          <div
            className="flex flex-col sm:flex-row flex-wrap gap-3 hero-reveal"
            style={{ animationDelay: `${typingTotalMs + 500}ms` }}
          >
            <button
              data-testid="hero-cta-primary"
              onClick={() => scrollTo("#contacto")}
              className="inline-flex items-center justify-center px-8 py-3.5 bg-[#2D4A2D] text-[#F5F0E8] text-xs uppercase tracking-[0.25em] hover:bg-[#C8A96E] hover:text-[#1A1A18] transition-all duration-300"
              style={{ fontFamily: "Lato, sans-serif" }}
            >
              {t.hero.cta1}
            </button>
            <button
              data-testid="hero-cta-secondary"
              onClick={() => scrollTo("#espacio")}
              className="inline-flex items-center justify-center px-8 py-3.5 border border-[#F5F0E8]/60 text-[#F5F0E8] text-xs uppercase tracking-[0.25em] hover:bg-[#C8A96E] hover:border-[#C8A96E] hover:text-[#1A1A18] transition-all duration-300"
              style={{ fontFamily: "Lato, sans-serif" }}
            >
              {t.hero.cta2}
            </button>
            <a
              data-testid="hero-cta-dossier"
              href={DOSSIER_URL}
              download="Torre_Amat_Diptico.pdf"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center justify-center gap-2 px-8 py-3.5 border border-[#C8A96E]/70 text-[#C8A96E] text-xs uppercase tracking-[0.25em] hover:bg-[#C8A96E] hover:text-[#1A1A18] transition-all duration-300"
              style={{ fontFamily: "Lato, sans-serif" }}
            >
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>
              {t.espacio.download_diptico}
            </a>
          </div>

          {/* Private note */}
          <p
            data-testid="hero-private-note"
            className="mt-5 text-[10px] uppercase tracking-[0.3em] text-[#F5F0E8]/35 hero-reveal"
            style={{
              fontFamily: "Lato, sans-serif",
              animationDelay: `${typingTotalMs + 700}ms`,
            }}
          >
            {t.hero.private_note}
          </p>
        </div>
      </div>

      {/* Scroll indicator */}
      <div className="absolute bottom-8 right-8 md:right-12 hidden md:flex flex-col items-center gap-2">
        <div className="w-px h-16 bg-[#C8A96E]/50 scroll-line" />
      </div>
    </section>
  );
}
